import {
    useCreativeDeleteById,
    useCreativesQuery,
    useCreativeUpdateNameById,
    useSetLinkCreativeMutation,
    type ICreative,
} from '@/shared/api'
import { WorkspaceWithLinkDescriptor } from '@synapex/shared2/entities'

import { useOptimisticValue } from '@/shared/hooks'

import { CreativeItem, type CreativeItemProps } from './CreativeItem'
import { useEditCreativeItem } from './useEditCreativeItem'
import { useCallback } from 'react'

const useCreativeMutations = ({
    onToastMessage,
    ...props
}: Pick<CreativesListProps, 'groupId' | 'onToastMessage'> &
    WorkspaceWithLinkDescriptor) => {
    const updateNameById = useCreativeUpdateNameById(props, {
        onSuccess: () => onToastMessage('Название обновлено'),
        onError: () => onToastMessage('Ошибка при обновлении названия'),
    })
    const deleteById = useCreativeDeleteById(props, {
        onSuccess: () => onToastMessage('Креатив удален'),
        onError: () => onToastMessage('Ошибка при удалении креатива'),
    })
    const setLinkCreative = useSetLinkCreativeMutation(props, {
        onError: (_, e) =>
            onToastMessage(
                `Ошибка: ${e instanceof Error ? e.message : 'Неизвестно'}`
            ),
    })

    return {
        updateNameByIdMutate: updateNameById.mutate,
        deleteByIdMutate: deleteById.mutate,
        setLinkCreativeMutate: setLinkCreative.mutate,
    }
}

export type CreativesListProps = {
    groupId: number
    activeCreative: ICreative | null
    onToastMessage: (value: string) => void
} & Pick<CreativeItemProps, 'onSelectActiveCreative'> &
    WorkspaceWithLinkDescriptor

export const CreativesList = ({
    activeCreative: actualActiveCreative,
    onSelectActiveCreative: onSelectActiveProp,
    ...props
}: CreativesListProps) => {
    const { data = [] } = useCreativesQuery(props, {
        refetchOnMount: true,
        refetchOnWindowFocus: false,
    })

    const mutations = useCreativeMutations(props)

    const editCreativeItem = useEditCreativeItem()

    const {
        value: activeCreative,
        setOptimisticValue: setOptimisticActiveCreative,
    } = useOptimisticValue(
        actualActiveCreative,
        useCallback(
            (a: ICreative | null, b: ICreative | null) => a?.id === b?.id,
            []
        )
    )

    const onSelectActive = (newActive: ICreative) => {
        setOptimisticActiveCreative(newActive)
        if (onSelectActiveProp) {
            onSelectActiveProp(newActive)
        }
    }

    if (data.length === 0 && !activeCreative) {
        return (
            <div className="font-inter rounded-[6px] bg-[#F2F6FD] px-[7.5px] py-[10px] text-[10px]">
                Чтобы выбрать креатив – <br /> перешлите его в бот Synapex
            </div>
        )
    }

    let activeCreativeToRender: ICreative | null = activeCreative
    let restCreativesToRender: ICreative[] = []
    if (activeCreative) {
        //ICreative fetched by useCreativesQuery has some additional fields(isEditable)
        //that ICreative attached to link hasn't
        for (const creative of data) {
            if (creative.id === activeCreative.id) {
                activeCreativeToRender = creative
            } else {
                restCreativesToRender.push(creative)
            }
        }
    } else {
        activeCreativeToRender = null
        restCreativesToRender = data
    }

    return (
        <>
            {activeCreativeToRender && (
                <>
                    <CreativeItem
                        {...editCreativeItem}
                        {...mutations}
                        active
                        data={activeCreativeToRender}
                    />
                    <div className="my-[4px] h-[1px] w-full bg-[#E9E9ED]" />
                </>
            )}
            {restCreativesToRender.map((creativeAvailableForSelection) => {
                return (
                    <CreativeItem
                        key={creativeAvailableForSelection.id}
                        {...editCreativeItem}
                        {...mutations}
                        onSelectActiveCreative={onSelectActive}
                        data={creativeAvailableForSelection}
                    />
                )
            })}
        </>
    )
}
